/**
 * APP 商城容器 query
 * 站点 id、apilevel：写入 localStorage，URL 上有且与本地不同才更新。
 * 其它 query（type 等）仍进 session。不修改链接、不拦截跳转。
 *
 * 用法：
 *   window.rrMallContainerQuery.getPersistedQuery()
 *   window.rrMallContainerQuery.getPersistedMallId()
 *   window.rrMallContainerQuery.getPersistedApiLevel()
 *   window.rrMallContainerQuery.navigateAppHomeResetHistory('/pages/app-store')
 */
(function (global) {
  'use strict';

  var STORAGE_KEY = '__rr_mall_container_query__';
  var MALL_ID_LOCAL_KEY = '__rr_mall_token_id__';
  var HOME_RESET_FLAG = '__rr_mall_home_history_reset__';
  /** 协议常用字段（业务可读） */
  var CORE_KEYS = ['id', 'type'];
  var APP_HOME_PATH_RE = /\/pages\/app-store$/i;
  var PROTOCOL_APILEVEL = 40660;
  var TIER_STORAGE_KEY = '__rr_protocol_tier__';
  var TIER_REASON_KEY = '__rr_protocol_tier_reason__';
  var TIER_APILEVEL_KEY = '__rr_mall_apilevel__';
  var TIER_UPDATED_AT_KEY = '__rr_protocol_tier_updated_at__';

  function isAppWebView() {
    return (
      typeof global.__rrMallIsAppWebView__ === 'function' &&
      global.__rrMallIsAppWebView__(global.navigator && global.navigator.userAgent)
    );
  }

  function isAppHomePage() {
    var path = (global.location && global.location.pathname) || '';
    return APP_HOME_PATH_RE.test(path.replace(/\/$/, '') || path);
  }

  /** 读取 search 上全部 query（非仅 CORE_KEYS） */
  function readAllFromSearch(search) {
    var out = {};
    try {
      var params = new URLSearchParams(search || global.location.search);
      params.forEach(function (val, key) {
        if (key && val != null && val !== '') {
          out[key] = val;
        }
      });
    } catch (e) {}
    return out;
  }

  function saveToStorage(query) {
    if (!query || Object.keys(query).length === 0) return;
    try {
      global.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(query));
    } catch (e) {}
  }

  function loadFromStorage() {
    try {
      var raw = global.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  function hasQueryParams(query) {
    return query && Object.keys(query).length > 0;
  }

  function parseApiLevel(raw) {
    if (raw == null || raw === '') return NaN;
    return parseInt(String(raw).replace(/^\s+|\s+$/g, ''), 10);
  }

  function getLocalItem(key) {
    try {
      var raw = global.localStorage.getItem(key);
      return raw != null && raw !== '' ? raw : null;
    } catch (e) {
      return null;
    }
  }

  function setLocalItem(key, value) {
    try {
      global.localStorage.setItem(key, String(value));
      return true;
    } catch (e) {
      return false;
    }
  }

  function getPersistedMallId() {
    var raw = getLocalItem(MALL_ID_LOCAL_KEY);
    if (raw == null) return null;
    var n = parseInt(raw, 10);
    return isNaN(n) ? null : n;
  }

  function getPersistedApiLevel() {
    var raw = getLocalItem(TIER_APILEVEL_KEY);
    if (raw == null) return null;
    var n = parseApiLevel(raw);
    return isNaN(n) ? null : n;
  }

  function persistMallIdIfChanged(id) {
    if (!isAppWebView() || id == null || id === '') return false;
    var next = String(id);
    var prev = getLocalItem(MALL_ID_LOCAL_KEY);
    if (prev === next) return false;
    if (!setLocalItem(MALL_ID_LOCAL_KEY, next)) return false;
    try {
      console.log('[MallContainerQuery] id updated', { from: prev, to: next });
    } catch (e) {}
    return true;
  }

  function persistApiLevelIfChanged(level) {
    if (!isAppWebView()) return false;
    var n = parseApiLevel(level);
    if (isNaN(n)) return false;
    var next = String(n);
    var prev = getLocalItem(TIER_APILEVEL_KEY);
    var changed = prev !== next;
    if (changed) {
      setLocalItem(TIER_APILEVEL_KEY, next);
      setLocalItem(TIER_UPDATED_AT_KEY, String(Date.now()));
      try {
        console.log('[MallContainerQuery] apilevel updated', { from: prev, to: next });
      } catch (e) {}
    }
    if (n >= PROTOCOL_APILEVEL && getLocalItem(TIER_STORAGE_KEY) !== 'r66') {
      setLocalItem(TIER_STORAGE_KEY, 'r66');
      setLocalItem(TIER_REASON_KEY, 'url_apilevel');
      if (!changed) setLocalItem(TIER_UPDATED_AT_KEY, String(Date.now()));
      changed = true;
    }
    return changed;
  }

  /** URL 上有 id / apilevel 时，和 localStorage 不同才写 */
  function persistStableParamsFromUrl() {
    if (!isAppWebView()) return;
    var fromUrl = readAllFromSearch();
    if (fromUrl.id != null && fromUrl.id !== '') persistMallIdIfChanged(fromUrl.id);
    if (fromUrl.apilevel) persistApiLevelIfChanged(fromUrl.apilevel);
  }

  /** 任意 APP 页：id/apilevel → localStorage；type 等仍进 session */
  function persistCoreKeysFromUrl() {
    if (!isAppWebView()) return;
    persistStableParamsFromUrl();
    var fromUrl = readAllFromSearch();
    var stored = loadFromStorage();
    var changed = false;
    CORE_KEYS.forEach(function (key) {
      if (key === 'id') return;
      if (fromUrl[key] == null || fromUrl[key] === '') return;
      stored[key] = String(fromUrl[key]);
      changed = true;
    });
    if (changed) saveToStorage(stored);
  }

  /** 首页：稳定字段走 localStorage；其它 query 仍进 session。子页读 localStorage 的 id/apilevel */
  function getPersistedQuery() {
    persistStableParamsFromUrl();
    var stored = loadFromStorage();
    var id = getPersistedMallId();
    var apilevel = getPersistedApiLevel();
    if (id != null) stored.id = String(id);
    if (apilevel != null) stored.apilevel = String(apilevel);
    if (isAppWebView() && isAppHomePage()) {
      var fromUrl = readAllFromSearch();
      if (hasQueryParams(fromUrl)) {
        saveToStorage(fromUrl);
        return Object.assign({}, stored, fromUrl);
      }
    }
    return stored;
  }

  function syncFromCurrentLocation() {
    if (!isAppWebView() || !isAppHomePage()) return;
    persistStableParamsFromUrl();
    var fromUrl = readAllFromSearch();
    if (hasQueryParams(fromUrl)) saveToStorage(fromUrl);
  }

  /**
   * 跳转 App 商城首页并尽量清空 WebView 历史栈（replace + 回首页后再 replaceState）
   * @param {string} homeUrl 一般为 /pages/app-store 完整 URL
   */
  function navigateAppHomeResetHistory(homeUrl) {
    if (!homeUrl) return;
    try {
      global.sessionStorage.setItem(HOME_RESET_FLAG, '1');
    } catch (e) {}

    try {
      if (global.history.length > 1) {
        global.history.go(1 - global.history.length);
        global.setTimeout(function () {
          global.location.replace(homeUrl);
        }, 0);
        return;
      }
    } catch (e) {}

    global.location.replace(homeUrl);
  }

  /** 落地首页后执行一次，配合 navigateAppHomeResetHistory */
  function applyHomeHistoryReset() {
    if (!isAppWebView() || !isAppHomePage()) return;
    try {
      if (global.sessionStorage.getItem(HOME_RESET_FLAG) !== '1') return;
      global.sessionStorage.removeItem(HOME_RESET_FLAG);
      if (global.history.replaceState) {
        global.history.replaceState(null, '', global.location.href);
      }
      if (global.history.length > 1) {
        global.history.go(1 - global.history.length);
        global.setTimeout(function () {
          try {
            if (global.history.replaceState) {
              global.history.replaceState(null, '', global.location.href);
            }
          } catch (e2) {}
        }, 0);
      }
    } catch (e) {}
  }

  var rrMallContainerQuery = {
    CORE_KEYS: CORE_KEYS.slice(),
    KEYS: CORE_KEYS.slice(),
    STORAGE_KEY: STORAGE_KEY,
    MALL_ID_LOCAL_KEY: MALL_ID_LOCAL_KEY,
    isAppWebView: isAppWebView,
    isAppHomePage: isAppHomePage,
    getPersistedQuery: getPersistedQuery,
    getPersistedMallId: getPersistedMallId,
    getPersistedApiLevel: getPersistedApiLevel,
    persistMallIdIfChanged: persistMallIdIfChanged,
    persistApiLevelIfChanged: persistApiLevelIfChanged,
    persistStableParamsFromUrl: persistStableParamsFromUrl,
    syncFromCurrentLocation: syncFromCurrentLocation,
    loadFromStorage: loadFromStorage,
    readAllFromSearch: readAllFromSearch,
    persistMallIdFromUrl: persistCoreKeysFromUrl,
    persistCoreKeysFromUrl: persistCoreKeysFromUrl,
    navigateAppHomeResetHistory: navigateAppHomeResetHistory,
    applyHomeHistoryReset: applyHomeHistoryReset,
  };

  global.rrMallContainerQuery = rrMallContainerQuery;

  if (isAppWebView()) {
    persistCoreKeysFromUrl();
    syncFromCurrentLocation();
    applyHomeHistoryReset();
  }
})(typeof window !== 'undefined' ? window : this);
